import { useState } from "react";
import { useParams, Link } from "react-router-dom";
import {
  ArrowLeft,
  ArrowRight,
  Mail,
  Phone,
  MapPin,
  ExternalLink,
  Download,
  Globe,
  Clock,
  User,
  UserPlus,
  Briefcase,
  Users,
  ChevronRight,
  Play,
  X,
} from "lucide-react";
import ASSOC_DETTAGLIO from "@/data/associazioniDettaglio";
import { ASSOCIAZIONI, BADGE_COLORS, MERCATO_COLORS } from "@/data/associazioniList";

/* Fallback avatar per il presidente quando la foto non carica */
function PresidenteAvatar({ nome, className = "" }) {
  const initials = nome
    .split(" ")
    .filter(Boolean)
    .map((n) => n[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();
  return (
    <div
      className={`flex items-center justify-center text-white font-bold text-xl font-['Encode_Sans_Semi_Condensed'] bg-[var(--confcom-blue)] ${className}`}
    >
      {initials}
    </div>
  );
}

export default function AssociazioneDettaglioPage() {
  const { id } = useParams();
  const [imgError, setImgError] = useState(false);
  const [lightboxSrc, setLightboxSrc] = useState(null);

  const dettaglio = ASSOC_DETTAGLIO.find((a) => a.id === id);
  const base = ASSOCIAZIONI.find((a) => a.id === id);

  /* ── 404 ─────────────────────────────────────────────────────── */
  if (!dettaglio || !base) {
    return (
      <main id="main-content" className="py-24 text-center">
        <div className="max-w-7xl mx-auto px-4">
          <h1 className="text-2xl font-bold text-[var(--confcom-text-primary)] font-['Encode_Sans_Semi_Condensed'] mb-4">
            Associazione non trovata
          </h1>
          <Link
            to="/associazioni"
            className="inline-flex items-center gap-2 text-[var(--confcom-blue)] font-medium hover:underline"
          >
            <ArrowLeft size={16} /> Torna alle associazioni
          </Link>
        </div>
      </main>
    );
  }

  const badgeClass =
    BADGE_COLORS[base.tipologia] || "bg-gray-100 text-gray-600";
  const mercatoColors = base.mercato ? MERCATO_COLORS[base.mercato] : null;

  /* Altre 3 associazioni della stessa tipologia, poi altre */
  const related = ASSOCIAZIONI.filter((a) => a.id !== id)
    .sort(
      (a, b) =>
        (b.tipologia === base.tipologia ? 1 : 0) -
        (a.tipologia === base.tipologia ? 1 : 0)
    )
    .slice(0, 3);

  return (
    <main id="main-content" data-testid="associazione-dettaglio-page">

      {/* ── HERO ─────────────────────────────────────────────────── */}
      <section
        className="relative overflow-hidden"
        style={{ background: "var(--confcom-blue)" }}
      >
        <div
          className="absolute inset-0 opacity-10"
          style={{
            backgroundImage: "url('/hero-pattern.svg')",
            backgroundSize: "cover",
          }}
        />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16 relative">

          {/* Back link */}
          <Link
            to="/associazioni"
            className="inline-flex items-center gap-2 text-sm text-white/70 hover:text-white mb-8 transition-colors"
            data-testid="back-to-associazioni"
          >
            <ArrowLeft size={16} /> Tutte le associazioni
          </Link>

          {/* Logo + Nome */}
          <div className="flex flex-col sm:flex-row items-start gap-8">

            {/* Logo su sfondo bianco */}
            {base.logo && (
              <div className="shrink-0 w-36 h-36 md:w-48 md:h-48 bg-white rounded-2xl shadow-lg flex items-center justify-center p-4">
                <img
                  src={base.logo}
                  alt={`Logo ${base.nome}`}
                  className="max-h-full max-w-full object-contain"
                />
              </div>
            )}

            {/* Testo */}
            <div className="flex-1">
              {/* Badge tipologia + mercato */}
              <div className="flex flex-wrap gap-2 mb-3">
                <span
                  className={`text-[11px] font-bold uppercase tracking-wide px-2.5 py-1 rounded-full ${badgeClass}`}
                >
                  {base.tipologia}
                </span>
                {base.mercato && mercatoColors && (
                  <span
                    className={`text-[11px] font-bold uppercase tracking-wide px-2.5 py-1 rounded-full ${mercatoColors.badge}`}
                  >
                    {base.mercato}
                  </span>
                )}
              </div>

              <h1
                className="text-3xl md:text-4xl lg:text-[2.75rem] font-bold text-white font-['Encode_Sans_Semi_Condensed'] tracking-tight leading-tight"
                data-testid="associazione-nome"
              >
                {base.nome}
              </h1>

              {base.descrizione && (
                <p className="mt-3 text-white/75 text-base max-w-2xl leading-relaxed">
                  {base.descrizione}
                </p>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* ── CONTENUTO PRINCIPALE ─────────────────────────────────── */}
      <section className="py-14 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-3 gap-12">

            {/* ── Colonna principale (2/3) ───────────────────────── */}
            <div className="lg:col-span-2" style={{ display: 'flex', flexDirection: 'column', gap: '3rem' }}>

              {/* Chi siamo */}
              {dettaglio.intro && dettaglio.intro.length > 0 && (
                <div>
                  <h2 className="text-xl font-bold text-[var(--confcom-text-primary)] font-['Encode_Sans_Semi_Condensed'] mb-5">
                    Chi siamo
                  </h2>
                  <div>
                    {dettaglio.intro.map((para, i) => (
                      <p
                        key={i}
                        className="text-[var(--confcom-text-secondary)] leading-relaxed mb-4"
                      >
                        {para}
                      </p>
                    ))}
                  </div>
                </div>
              )}

              {/* Servizi */}
              {dettaglio.servizi && dettaglio.servizi.length > 0 && (
                <div>
                  <h2 className="text-xl font-bold text-[var(--confcom-text-primary)] font-['Encode_Sans_Semi_Condensed'] mb-5 flex items-center gap-2">
                    <Briefcase
                      size={20}
                      className="text-[var(--confcom-blue)]"
                      aria-hidden="true"
                    />
                    {dettaglio.serviziTitolo || "Servizi agli associati"}
                  </h2>
                  <div className="grid sm:grid-cols-2 gap-3">
                    {dettaglio.servizi.map((s, i) => (
                      <div
                        key={i}
                        className="flex items-start gap-3 p-4 bg-[#EEF4FB] rounded-xl border border-[#C8DDEF]"
                      >
                        <div className="w-2 h-2 rounded-full shrink-0 mt-1.5 bg-[var(--confcom-blue)]" />
                        <span className="text-sm text-[var(--confcom-text-primary)] leading-relaxed">
                          {s}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Presidente */}
              {dettaglio.presidente && (
                <div>
                  <h2 className="text-xl font-bold text-[var(--confcom-text-primary)] font-['Encode_Sans_Semi_Condensed'] mb-6 flex items-center gap-2">
                    <User
                      size={20}
                      className="text-[var(--confcom-blue)]"
                      aria-hidden="true"
                    />
                    Presidente
                  </h2>

                  {/* Foto + nome */}
                  <div className="flex items-start gap-5 mb-5">
                    {dettaglio.presidente.foto && !imgError ? (
                      <button
                        onClick={() => {
                          setLightboxSrc(dettaglio.presidente.foto);
                        }}
                        className="relative shrink-0 rounded-xl overflow-hidden cursor-zoom-in hover:ring-2 hover:ring-[var(--confcom-blue)] hover:ring-offset-2 transition-all"
                        aria-label={`Ingrandisci foto ${dettaglio.presidente.nome}`}
                        data-testid="presidente-foto-btn"
                      >
                        <img
                          src={dettaglio.presidente.foto}
                          alt={`Foto ${dettaglio.presidente.nome}`}
                          className="w-20 h-20 md:w-24 md:h-24 object-cover block"
                          onError={() => setImgError(true)}
                        />
                      </button>
                    ) : (
                      <PresidenteAvatar
                        nome={dettaglio.presidente.nome}
                        className="w-20 h-20 md:w-24 md:h-24 rounded-xl shrink-0"
                      />
                    )}
                    <div>
                      <div className="text-lg font-bold text-[var(--confcom-text-primary)] font-['Encode_Sans_Semi_Condensed']">
                        {dettaglio.presidente.nome}
                      </div>
                      <div className="text-sm text-[var(--confcom-blue)] font-medium mt-0.5">
                        Presidente
                      </div>
                    </div>
                  </div>

                  {/* Bio */}
                  {dettaglio.presidente.bio &&
                    dettaglio.presidente.bio.length > 0 && (
                      <div>
                        {dettaglio.presidente.bio.map((para, i) => (
                          <p
                            key={i}
                            className="text-sm text-[var(--confcom-text-secondary)] leading-relaxed"
                          >
                            {para}
                          </p>
                        ))}
                      </div>
                    )}
                </div>
              )}

              {/* Video */}
              {dettaglio.video && (
                <div>
                  <h2 className="text-xl font-bold text-[var(--confcom-text-primary)] font-['Encode_Sans_Semi_Condensed'] mb-5 flex items-center gap-2">
                    <Play
                      size={20}
                      className="text-[var(--confcom-blue)]"
                      aria-hidden="true"
                    />
                    {dettaglio.video.sezioneTitolo || "Video"}
                  </h2>
                  <div className="relative pb-[56.25%] h-0 overflow-hidden rounded-xl bg-black">
                    <iframe
                      src={`https://www.youtube.com/embed/${dettaglio.video.youtubeId}`}
                      title={dettaglio.video.titolo}
                      className="absolute inset-0 w-full h-full"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                      data-testid="video-embed"
                    />
                  </div>
                  {dettaglio.video.titolo && (
                    <p className="text-sm text-[var(--confcom-text-muted)] mt-2 italic">
                      {dettaglio.video.titolo}
                    </p>
                  )}
                </div>
              )}

              {/* Consiglio Direttivo */}
              {dettaglio.consiglio && dettaglio.consiglio.length > 0 && (
                <div>
                  <h2 className="text-xl font-bold text-[var(--confcom-text-primary)] font-['Encode_Sans_Semi_Condensed'] mb-5 flex items-center gap-2">
                    <Users
                      size={20}
                      className="text-[var(--confcom-blue)]"
                      aria-hidden="true"
                    />
                    Consiglio Direttivo
                  </h2>
                  <div className="rounded-xl border border-[var(--confcom-border)] overflow-hidden divide-y divide-[var(--confcom-border)]">
                    {dettaglio.consiglio.map((membro, i) => (
                      <div
                        key={i}
                        className="flex items-center justify-between px-5 py-3.5 hover:bg-[#F8FAFD] transition-colors"
                        data-testid={`membro-consiglio-${i}`}
                      >
                        <span className="text-sm font-semibold text-[var(--confcom-text-primary)]">
                          {membro.nome}
                        </span>
                        <span className="text-xs text-[var(--confcom-text-muted)] text-right max-w-[58%] leading-snug">
                          {membro.ruolo}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Link utili */}
              {dettaglio.links && dettaglio.links.length > 0 && (
                <div>
                  <h2 className="text-xl font-bold text-[var(--confcom-text-primary)] font-['Encode_Sans_Semi_Condensed'] mb-5">
                    Link utili
                  </h2>
                  <div className="flex flex-wrap gap-3">
                    {dettaglio.links.map((link, i) => (
                      <a
                        key={i}
                        href={link.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-semibold border transition-all ${
                          link.tipo === "download"
                            ? "bg-[var(--confcom-blue)] text-white border-[var(--confcom-blue)] hover:bg-[#002a5a]"
                            : "bg-white text-[var(--confcom-blue)] border-[var(--confcom-blue)]/40 hover:border-[var(--confcom-blue)] hover:bg-[#EEF4FB]"
                        }`}
                        data-testid={`link-utile-${i}`}
                      >
                        {link.tipo === "download" ? (
                          <Download size={15} aria-hidden="true" />
                        ) : (
                          <ExternalLink size={15} aria-hidden="true" />
                        )}
                        {link.label}
                      </a>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* ── Sidebar contatti (1/3) ────────────────────────── */}
            <div className="lg:col-span-1">
              <div
                className="bg-white rounded-2xl border border-[var(--confcom-border)] p-6 sticky top-[104px] lg:top-[168px] shadow-sm"
                data-testid="sidebar-contatti"
              >
                <h3 className="text-lg font-bold text-[var(--confcom-text-primary)] font-['Encode_Sans_Semi_Condensed'] mb-5">
                  Contatti
                </h3>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>

                  {/* Email */}
                  {dettaglio.contatti.email && (
                    <a
                      href={`mailto:${dettaglio.contatti.email}`}
                      className="flex items-start gap-3 group"
                      data-testid="contatto-email"
                    >
                      <Mail
                        size={17}
                        className="text-[var(--confcom-blue)] mt-0.5 shrink-0"
                        aria-hidden="true"
                      />
                      <div>
                        <div className="text-[11px] font-bold uppercase tracking-wider text-[var(--confcom-text-muted)]">
                          Email
                        </div>
                        <div className="text-sm font-medium text-[var(--confcom-blue)] group-hover:underline mt-0.5 break-all">
                          {dettaglio.contatti.email}
                        </div>
                      </div>
                    </a>
                  )}

                  {/* Telefono */}
                  {dettaglio.contatti.tel && (
                    <div className="flex items-start gap-3" data-testid="contatto-tel">
                      <Phone
                        size={17}
                        className="text-[var(--confcom-blue)] mt-0.5 shrink-0"
                        aria-hidden="true"
                      />
                      <div>
                        <div className="text-[11px] font-bold uppercase tracking-wider text-[var(--confcom-text-muted)]">
                          Telefono
                        </div>
                        <div className="text-sm font-medium text-[var(--confcom-text-primary)] mt-0.5">
                          {dettaglio.contatti.tel}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Indirizzo */}
                  {dettaglio.contatti.indirizzo && (
                    <div className="flex items-start gap-3">
                      <MapPin
                        size={17}
                        className="text-[var(--confcom-blue)] mt-0.5 shrink-0"
                        aria-hidden="true"
                      />
                      <div>
                        <div className="text-[11px] font-bold uppercase tracking-wider text-[var(--confcom-text-muted)]">
                          Indirizzo
                        </div>
                        <div className="text-sm text-[var(--confcom-text-secondary)] mt-0.5 leading-snug">
                          {dettaglio.contatti.indirizzo}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Orari */}
                  {dettaglio.contatti.orari && (
                    <div className="flex items-start gap-3">
                      <Clock
                        size={17}
                        className="text-[var(--confcom-blue)] mt-0.5 shrink-0"
                        aria-hidden="true"
                      />
                      <div>
                        <div className="text-[11px] font-bold uppercase tracking-wider text-[var(--confcom-text-muted)]">
                          Orari
                        </div>
                        <div className="text-xs text-[var(--confcom-text-secondary)] mt-0.5 leading-relaxed">
                          {dettaglio.contatti.orari}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Sito web */}
                  {dettaglio.contatti.sito && (
                    <a
                      href={dettaglio.contatti.sito}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-start gap-3 group"
                      data-testid="contatto-sito"
                    >
                      <Globe
                        size={17}
                        className="text-[var(--confcom-blue)] mt-0.5 shrink-0"
                        aria-hidden="true"
                      />
                      <div>
                        <div className="text-[11px] font-bold uppercase tracking-wider text-[var(--confcom-text-muted)]">
                          Sito web
                        </div>
                        <div className="text-sm font-medium text-[var(--confcom-blue)] group-hover:underline mt-0.5 break-all">
                          {dettaglio.contatti.sitoLabel || dettaglio.contatti.sito}
                        </div>
                      </div>
                    </a>
                  )}

                  {/* Social */}
                  {dettaglio.contatti.social &&
                    dettaglio.contatti.social.map((s, i) => (
                      <a
                        key={i}
                        href={s.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-start gap-3 group"
                        data-testid={`contatto-social-${i}`}
                      >
                        <ExternalLink
                          size={17}
                          className="text-[var(--confcom-blue)] mt-0.5 shrink-0"
                          aria-hidden="true"
                        />
                        <div>
                          <div className="text-[11px] font-bold uppercase tracking-wider text-[var(--confcom-text-muted)]">
                            {s.label}
                          </div>
                          <div className="text-sm text-[var(--confcom-blue)] group-hover:underline mt-0.5 break-all">
                            {s.url
                              .replace("https://", "")
                              .replace("http://", "")}
                          </div>
                        </div>
                      </a>
                    ))}
                </div>

                {/* CTA */}
                <div className="mt-6 pt-5 border-t border-[var(--confcom-border)] space-y-3">
                  {dettaglio.contatti.email && (
                    <a
                      href={`mailto:${dettaglio.contatti.email}?subject=Richiesta informazioni - ${base.nome}`}
                      className="flex items-center justify-center gap-2 w-full py-3 rounded-xl text-sm font-semibold text-white bg-[var(--confcom-blue)] hover:bg-[#002a5a] transition-colors"
                      data-testid="cta-email"
                    >
                      <Mail size={16} aria-hidden="true" /> Richiedi informazioni
                    </a>
                  )}
                  <Link
                    to="/associati-ora"
                    className="flex items-center justify-center gap-2 w-full py-3 rounded-xl text-sm font-semibold text-[var(--confcom-blue)] border border-[var(--confcom-blue)] hover:bg-[var(--confcom-bg-blue-light)] transition-colors"
                    data-testid="cta-associati-ora"
                  >
                    <UserPlus size={16} aria-hidden="true" /> Associati ora
                  </Link>
                </div>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* ── ALTRE ASSOCIAZIONI ───────────────────────────────────── */}
      {related.length > 0 && (
        <section
          className="py-14 bg-[#F8FAFD]"
          aria-labelledby="related-title"
        >
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2
              id="related-title"
              className="text-2xl font-bold text-[var(--confcom-text-primary)] font-['Encode_Sans_Semi_Condensed'] mb-8"
            >
              Altre associazioni
            </h2>

            <div className="grid md:grid-cols-3 gap-6">
              {related.map((a) => (
                <Link
                  key={a.id}
                  to={`/associazioni/${a.id}`}
                  className="group bg-white rounded-xl border border-[var(--confcom-border)] overflow-hidden hover:border-[var(--confcom-blue)]/40 hover:shadow-md transition-all"
                  data-testid={`related-card-${a.id}`}
                >
                  {/* Logo */}
                  <div className="h-36 bg-white flex items-center justify-center p-5 border-b border-[var(--confcom-border)]">
                    {a.logo ? (
                      <img
                        src={a.logo}
                        alt={`Logo ${a.nome}`}
                        className="max-h-full max-w-[80%] object-contain"
                        loading="lazy"
                      />
                    ) : (
                      <span className="text-3xl font-bold text-[var(--confcom-blue)] font-['Encode_Sans_Semi_Condensed']">
                        {a.nome.slice(0, 2).toUpperCase()}
                      </span>
                    )}
                  </div>

                  {/* Testo */}
                  <div className="p-4">
                    <span
                      className={`text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full ${
                        BADGE_COLORS[a.tipologia] || "bg-gray-100 text-gray-600"
                      }`}
                    >
                      {a.tipologia}
                    </span>
                    <h3 className="text-sm font-bold text-[var(--confcom-text-primary)] font-['Encode_Sans_Semi_Condensed'] group-hover:text-[var(--confcom-blue)] transition-colors line-clamp-2 mt-2 mb-2">
                      {a.nome}
                    </h3>
                    <span className="inline-flex items-center gap-1 text-xs text-[var(--confcom-blue)] font-semibold">
                      Scopri <ChevronRight size={12} />
                    </span>
                  </div>
                </Link>
              ))}
            </div>

            <div className="mt-8 text-center">
              <Link
                to="/associazioni"
                className="inline-flex items-center gap-2 text-sm font-semibold text-[var(--confcom-blue)] hover:underline"
                data-testid="link-tutte-associazioni"
              >
                <ArrowLeft size={14} /> Vedi tutte le associazioni
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* ── LIGHTBOX FOTO PRESIDENTE ───────────────────────────── */}
      {lightboxSrc && (
        <div
          className="fixed inset-0 bg-black/85 z-[200] flex items-center justify-center p-4 backdrop-blur-sm"
          onClick={() => setLightboxSrc(null)}
          data-testid="lightbox-overlay"
        >
          <button
            className="fixed top-5 right-5 text-white/80 hover:text-white bg-white/10 hover:bg-white/25 rounded-full p-2.5 transition-all"
            onClick={() => setLightboxSrc(null)}
            aria-label="Chiudi"
            data-testid="lightbox-close"
          >
            <X size={24} />
          </button>
          <img
            src={lightboxSrc}
            alt="Foto presidente"
            className="max-h-[85vh] max-w-[85vw] object-contain rounded-2xl shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </main>
  );
}
